import { TextColumns } from "@phosphor-icons/react";
import { useMemo } from "react";

import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import { typedObjectEntries } from "common/helpers/utils";

import type { JSX } from "react";

function areEqualSets<TagKey extends string>(
  currentKeys: TagKey[],
  baselineKeys: TagKey[],
) {
  if (currentKeys.length !== baselineKeys.length) return false;

  const currentKeySet = new Set(currentKeys);

  if (currentKeySet.size !== baselineKeys.length) return false;

  for (const baselineKey of baselineKeys) {
    if (!currentKeySet.has(baselineKey)) return false;
  }

  return true;
}

export default function TagSelectionDrawer<TagKey extends string>({
  tagKeys,
  setTagKeys,
  allTags,
}: {
  tagKeys: TagKey[];
  setTagKeys: (tagKeys: TagKey[]) => void;
  allTags: Record<
    TagKey,
    {
      icon: JSX.Element;
      label: string;
      isDefault?: true;
    }
  >;
}) {
  const defaultTagKeys = useMemo(() => {
    return typedObjectEntries(allTags)
      .filter(([, config]) => config.isDefault)
      .map(([key]) => key);
  }, [allTags]);

  const hasSelectionChanged = !areEqualSets(tagKeys, defaultTagKeys);

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button variant="outline-black" size="sm" leftIcon={<TextColumns />}>
          Items
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader
          title="Account items"
          actionComponent={
            <Button
              variant="link-purple"
              disabled={!hasSelectionChanged}
              onClick={() => setTagKeys(defaultTagKeys)}
            >
              Reset
            </Button>
          }
        />

        <div className="flex gap-2 flex-wrap">
          {typedObjectEntries(allTags).map(([key, { icon, label }]) => {
            const isSelected = tagKeys.includes(key);

            return (
              <Button
                key={key}
                variant={isSelected ? "secondary-black" : "outline-black"}
                size="sm"
                onClick={() => {
                  if (isSelected) {
                    setTagKeys(
                      tagKeys.filter((visibleTagKey) => visibleTagKey !== key),
                    );
                  } else {
                    setTagKeys([...tagKeys, key]);
                  }
                }}
                leftIcon={icon}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
